import React from 'react';
import {
  Alert, Avatar, Badge, Button, ConfirmDialog, ErrorBoundaryCore, ErrorState, InnerPageHeader,
  SkeletonCard, useToast, phone,
} from '@parsina/framework';
import { Coins, KeyRound } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useShortcut } from '../../lib/shortcuts';
import { statusLabelKey } from '../../constants/subscription';
import { validUntil } from '../../lib/format';
import { SubscriptionBadge, TierBadge } from '../../components/SubscriptionBadge';
import SystemState from '../../components/SystemState';
import ActivationDialog from './ActivationDialog';
import SuspendDialog from './SuspendDialog';
import OverrideDialog from './OverrideDialog';
import GrantCreditsDialog from './GrantCreditsDialog';
import CountrySettingsCard from './CountrySettingsCard';
import {
  ClinicInfoRail, EventsSection, PeriodsSection, SubscriptionSection,
} from './sections';

const DETAIL_QUERY = `
  query ClinicDetail($clinicId: ID!) {
    clinicDetail(clinicId: $clinicId) {
      clinic {
        id name clinicType officePhone active createdAt currency ownerUserId
        subscriptionStatus entitled hasSubscription planCode capabilitySet tierNameFa
        currentPeriodEnd trialEndsAt
      }
      subscription {
        id status planCode capabilitySet tierNameFa origin graceDays notRenewing
        suspendedReason version createdAt
        currentPeriodStart currentPeriodEnd trialEndsAt graceEndsAt
        periods { id planCode periodStart periodEnd priceRial currency source activatedBy activatedAt note }
      }
      events { id eventType fromStatus toStatus actor payload occurredAt }
      staffCount
    }
    plans { code nameFa currency priceRial priceToman periodMonths capabilitySet tierNameFa }
  }
`;

const ACTIVATE = `
  mutation Activate(
    $clinicId: ID!, $planCode: String!, $periodStart: String, $periodEnd: String, $note: String
  ) {
    activateSubscription(
      clinicId: $clinicId, planCode: $planCode,
      periodStart: $periodStart, periodEnd: $periodEnd, note: $note
    ) { id status }
  }
`;
const SUSPEND = `
  mutation Suspend($clinicId: ID!, $reason: String!) {
    suspendSubscription(clinicId: $clinicId, reason: $reason) { id status }
  }
`;
const UNSUSPEND = `
  mutation Unsuspend($clinicId: ID!) {
    unsuspendSubscription(clinicId: $clinicId) { id status }
  }
`;
const GRANT_OVERRIDE = `
  mutation GrantOverride(
    $clinicId: ID!, $capability: String!, $granted: Boolean!, $reason: String, $expiresAt: String
  ) {
    grantOverride(
      clinicId: $clinicId, capability: $capability, granted: $granted,
      reason: $reason, expiresAt: $expiresAt
    ) { id status }
  }
`;
const GRANT_CREDITS = `
  mutation GrantCredits($ownerId: ID!, $amount: Int!, $note: String, $requestId: ID) {
    grantCredits(ownerId: $ownerId, amount: $amount, note: $note, requestId: $requestId) {
      ownerId amount reference balance
    }
  }
`;

/**
 * Archetype C (Record) — one clinic.
 *
 * ── THE HEADER SURVIVES EVERY STATE ─────────────────────────────────────────
 * Loading and error replace the BODY only. Before this they replaced the whole
 * page including the header, so the way back disappeared exactly when a failed
 * load made an operator want it, and the layout jumped when the data arrived.
 *
 * ── THE IDENTITY BAND CARRIES THE VERDICT ───────────────────────────────────
 * Status, tier, entitlement and the expiry date are in zone ① (§2.3-C), not buried
 * in the first card. They are what the operator opened the page to read; making
 * them the heading means the answer is above the fold on every screen size.
 *
 * ── ACTIONS ARE NEUTRAL AT REST ─────────────────────────────────────────────
 * «تعلیق» used to be a solid red button sitting on a healthy clinic's page. §10's
 * colour budget says red at rest means a genuine problem, so a permanently-red
 * control on every record teaches operators to ignore red. The destructive styling
 * appears in the dialog, where the decision is actually made.
 */
export default function ClinicDetail({ clinicId, navigate }) {
  const { t, formatDate, formatNumber } = useTranslation();
  const toast = useToast();
  useDocumentTitle('nav.title.clinic');

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);

  // Which dialog is open, and the state of the write it owns. One `pending`/
  // `dialogError` pair rather than one per dialog: only one can be open at a time
  // (§5.2 — no stacking), so two would be two ways to describe one fact.
  const [dialog, setDialog] = React.useState(null);   // activate | suspend | override | unsuspend | grant
  const [pending, setPending] = React.useState(false);
  const [dialogError, setDialogError] = React.useState(null);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(DETAIL_QUERY, { clinicId })
      .then(setData)
      .catch((e) => {
        // A clinic that does not exist is a WHOLE-PAGE state, not a region error:
        // there is no record to show around the message, and the useful action is
        // going back to the list rather than retrying a query that will fail
        // identically.
        if (e?.code === 'NOT_FOUND') setNotFound(true);
        else setError(errorMessage(e, t));
      })
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [clinicId, t]);

  React.useEffect(() => { load(); }, [load]);

  /**
   * Run a mutation with the §2.4 lifecycle: pending → (success | failure).
   *
   * On failure the dialog STAYS OPEN with its input and shows the reason inline;
   * only a success closes it. The predecessor closed first and awaited second, so
   * a failed write silently discarded whatever the operator had typed.
   */
  const run = async (mutation, variables, successKey) => {
    setPending(true);
    setDialogError(null);
    try {
      await gql(mutation, variables);
      setDialog(null);
      toast.success(t(successKey));
      load({ quiet: true });
    } catch (e) {
      setDialogError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const detail = data?.clinicDetail;
  const clinic = detail?.clinic;
  const subscription = detail?.subscription;

  /**
   * Credits to the clinic's owner. Same lifecycle as `run`, but the toast names
   * the amount and the owner's new balance — the result is the point — and
   * nothing on this page needs reloading: the wallet is not shown here.
   */
  const grantCredits = async (vars) => {
    setPending(true);
    setDialogError(null);
    try {
      const res = await gql(GRANT_CREDITS, { ownerId: clinic.ownerUserId, ...vars });
      const grant = res?.grantCredits;
      setDialog(null);
      toast.success(t('clinic.dialogs.grantCredits.success', {
        amount: formatNumber(grant?.amount ?? vars.amount),
        balance: grant?.balance == null ? t('common.value.none') : formatNumber(grant.balance),
      }));
    } catch (e) {
      setDialogError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const openActivate = () => { setDialogError(null); setDialog('activate'); };

  useShortcut('r', openActivate, {
    label: t('shortcuts.action.renew'),
    group: t('shortcuts.groups.clinic'),
    enabled: !!clinic,
  });

  // The same dialog under the name the button carries when there is nothing to
  // renew yet. Two keys for one action rather than one key whose label changes:
  // the `?` overlay lists what the screen's buttons actually say, and «تمدید» on
  // a clinic that has never had a subscription is a promise about a record that
  // does not exist.
  useShortcut('n', openActivate, {
    label: t('shortcuts.action.provision'),
    group: t('shortcuts.groups.clinic'),
    enabled: !!clinic && !clinic.hasSubscription,
  });

  if (notFound) {
    return (
      <SystemState
        code="404"
        titleKey="errors.system.clinicNotFound.title"
        adviceKey="errors.system.clinicNotFound.advice"
        testId="detail-not-found"
        actions={[{
          key: 'back',
          label: t('errors.system.clinicNotFound.action'),
          onClick: () => navigate('/clinics'),
        }]}
      />
    );
  }

  const suspended = subscription?.status === 'SUSPENDED';
  const provisioned = !!clinic?.hasSubscription;

  return (
    <div className="flex h-full flex-col bg-background" data-testid="clinic-detail">
      <InnerPageHeader
        // Renders from the route, so it is on screen during the first load and
        // during a failure — the way back is never the thing that disappears.
        title={clinic?.name || t('common.value.none')}
        onBack={() => navigate('/clinics')}
        backLabel={t('clinic.back')}
        avatar={clinic ? <Avatar name={clinic.name} size={40} /> : undefined}
        status={clinic ? <SubscriptionBadge clinic={clinic} /> : undefined}
        badges={clinic ? (
          <>
            <TierBadge clinic={clinic} />
            {subscription?.notRenewing ? (
              <Badge variant="warning">{t('clinic.alerts.notRenewing')}</Badge>
            ) : null}
            {clinic.active === false ? (
              <Badge variant="outline" title={t('clinics.inactiveHint')}>
                {t('clinics.inactive')}
              </Badge>
            ) : null}
          </>
        ) : undefined}
        meta={clinic ? [
          clinic.clinicType,
          // National form — the panel shows what an operator would dial, not the
          // E.164 the registry now stores. `phone.forDisplay` never blanks: an
          // unparseable number is still the only one we hold for this clinic.
          clinic.officePhone
            ? (
              <span key="phone" {...LTR_TEXT} className="tabular-nums">
                {phone.forDisplay(clinic.officePhone)}
              </span>
            )
            : null,
          clinic.createdAt ? t('clinic.meta.created', { date: formatDate(clinic.createdAt) }) : null,
          t('clinic.meta.staff', { count: detail?.staffCount || 0 }),
        ].filter(Boolean) : undefined}
        stats={clinic ? [
          {
            label: t('clinic.stats.until'),
            value: provisioned ? validUntil(subscription, { t, formatDate }) : t('common.value.none'),
          },
          {
            label: t('clinic.stats.entitled'),
            value: clinic.entitled ? t('clinics.entitled.yes') : t('clinics.entitled.no'),
            accent: !clinic.entitled,
          },
        ] : undefined}
        data-testid="detail-header"
      >
        {clinic ? (
          <>
            <Button onClick={openActivate} data-testid="detail-activate">
              {provisioned ? t('clinic.actions.activate') : t('clinic.actions.provision')}
            </Button>

            {/* Hidden on an un-provisioned clinic: there is nothing to suspend,
                and an override on a clinic the service already fails open for
                would grant nothing. A control that does nothing teaches operators
                not to trust the ones that do. */}
            {provisioned ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setDialogError(null); setDialog('override'); }}
                  data-testid="detail-override"
                >
                  <KeyRound size={16} aria-hidden />
                  {t('clinic.actions.override')}
                </Button>
                {suspended ? (
                  <Button
                    variant="outline"
                    onClick={() => { setDialogError(null); setDialog('unsuspend'); }}
                    data-testid="detail-unsuspend"
                  >
                    {t('clinic.actions.unsuspend')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => { setDialogError(null); setDialog('suspend'); }}
                    data-testid="detail-suspend"
                  >
                    {t('clinic.actions.suspend')}
                  </Button>
                )}
              </>
            ) : null}

            {/* For the owner's wallet, so only where the registry names an owner:
                a button that could grant to no one is not offered. */}
            {clinic.ownerUserId ? (
              <Button
                variant="outline"
                onClick={() => { setDialogError(null); setDialog('grant'); }}
                data-testid="detail-grant-credits"
              >
                <Coins size={16} aria-hidden />
                {t('clinic.actions.grantCredits')}
              </Button>
            ) : null}
          </>
        ) : null}
      </InnerPageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto w-full max-w-[1440px]">
          {loading ? (
            <div
              className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]"
              data-testid="detail-loading"
              data-state="loading"
              aria-busy="true"
            >
              <div className="space-y-5"><SkeletonCard /><SkeletonCard /><SkeletonCard /></div>
              <SkeletonCard />
            </div>
          ) : error ? (
            <ErrorState
              title={t('errors.region.clinic')}
              description={error}
              onRetry={load}
              retryLabel={t('common.actions.retry')}
              data-testid="detail-error"
            />
          ) : (
            <div
              className="transition-opacity"
              style={{ opacity: refreshing ? 0.45 : 1 }}
              aria-busy={refreshing || undefined}
            >
              {/* ② Alert zone, rationed and ordered by severity. */}
              <div className="mb-5 flex flex-col gap-3">
                {suspended ? (
                  <Alert
                    variant="error"
                    title={t('clinic.alerts.suspendedTitle')}
                    data-testid="detail-alert-suspended"
                    action={(
                      <Button variant="outline" size="sm" onClick={() => setDialog('unsuspend')}>
                        {t('clinic.actions.unsuspend')}
                      </Button>
                    )}
                  >
                    {t('clinic.alerts.suspendedBody', {
                      reason: subscription?.suspendedReason || t('common.value.none'),
                    })}
                  </Alert>
                ) : null}

                {/* The single most decisive fact on the page, and it was rendered
                    nowhere at all: a clinic can read ACTIVE and طلایی and still be
                    blocked. */}
                {provisioned && !clinic.entitled && !suspended ? (
                  <Alert
                    variant="warning"
                    title={t('clinic.alerts.notEntitledTitle')}
                    data-testid="detail-alert-entitlement"
                    action={(
                      <Button variant="outline" size="sm" onClick={openActivate}>
                        {t('clinic.actions.activate')}
                      </Button>
                    )}
                  >
                    {t('clinic.alerts.notEntitledBody', {
                      status: t(statusLabelKey(subscription?.status)),
                    })}
                  </Alert>
                ) : null}

                {!provisioned ? (
                  <Alert
                    variant="info"
                    data-testid="detail-alert-unprovisioned"
                    action={(
                      <Button variant="outline" size="sm" onClick={openActivate}>
                        {t('clinic.actions.provision')}
                      </Button>
                    )}
                  >
                    {t('clinic.alerts.unprovisioned')}
                  </Alert>
                ) : null}
              </div>

              {/* ⑤ P2 — sections in main, the registry's facts and the playbook in
                  the rail. The rail stacks under main at Compact width. */}
              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
                <div className="flex min-w-0 flex-col gap-5">
                  <SubscriptionSection clinic={clinic} subscription={subscription} />
                  {provisioned ? (
                    <>
                      <PeriodsSection periods={subscription?.periods} />
                      <EventsSection events={detail.events} />
                    </>
                  ) : null}
                  {/* The clinic's jurisdiction (PM-0218). Its own query and states,
                      so the record above never waits on — or fails with — the
                      country registry; and its own boundary, so even a crash in
                      it costs this card and not the record. */}
                  <ErrorBoundaryCore
                    resetKey={clinic.id}
                    renderFallback={(_error, reset) => (
                      <ErrorState
                        title={t('countries.clinicCard.error')}
                        onRetry={reset}
                        retryLabel={t('common.actions.retry')}
                        data-testid="clinic-country-crash"
                      />
                    )}
                  >
                    <CountrySettingsCard clinicId={clinic.id} />
                  </ErrorBoundaryCore>
                </div>
                <ClinicInfoRail clinic={clinic} />
              </div>
            </div>
          )}
        </div>
      </div>

      {clinic ? (
        <>
          <ActivationDialog
            open={dialog === 'activate'}
            onOpenChange={(open) => setDialog(open ? 'activate' : null)}
            clinic={clinic}
            plans={data.plans}
            pending={pending}
            error={dialogError}
            onSubmit={(vars) => run(
              ACTIVATE,
              { clinicId, ...vars },
              'clinic.dialogs.activate.success',
            )}
          />

          <SuspendDialog
            open={dialog === 'suspend'}
            onOpenChange={(open) => setDialog(open ? 'suspend' : null)}
            pending={pending}
            error={dialogError}
            onSubmit={({ reason }) => run(
              SUSPEND,
              { clinicId, reason },
              'clinic.dialogs.suspend.success',
            )}
          />

          <OverrideDialog
            open={dialog === 'override'}
            onOpenChange={(open) => setDialog(open ? 'override' : null)}
            pending={pending}
            error={dialogError}
            onSubmit={(vars) => run(
              GRANT_OVERRIDE,
              { clinicId, ...vars },
              'clinic.dialogs.override.success',
            )}
          />

          <GrantCreditsDialog
            open={dialog === 'grant'}
            onOpenChange={(open) => setDialog(open ? 'grant' : null)}
            clinic={clinic}
            pending={pending}
            error={dialogError}
            onSubmit={grantCredits}
          />

          {/* Lifting a suspension takes nothing but a decision, so it is a confirm
              rather than a form — and the consequence is stated, because the lost
              time is NOT credited back and an operator should know that before
              pressing. */}
          <ConfirmDialog
            open={dialog === 'unsuspend'}
            onOpenChange={(open) => setDialog(open ? 'unsuspend' : null)}
            title={t('clinic.dialogs.unsuspend.title', { name: clinic.name })}
            description={t('clinic.dialogs.unsuspend.body')}
            confirmLabel={t('clinic.dialogs.unsuspend.confirm')}
            cancelLabel={t('common.actions.cancel')}
            loading={pending}
            onConfirm={() => run(UNSUSPEND, { clinicId }, 'clinic.dialogs.unsuspend.success')}
            data-testid="detail-unsuspend-confirm"
          />
        </>
      ) : null}
    </div>
  );
}
